<template>
  <div>
    <!-- Phone numbers list -->
    <div v-if="loading" class="py-6 text-center text-sm text-gray-500">Loading phone numbers...</div>

    <div v-else>
      <div v-if="phoneNumbers.length > 0" class="divide-y divide-gray-100">
        <div
          v-for="num in phoneNumbers"
          :key="num.id"
          class="flex items-center justify-between py-3"
        >
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-gray-900 sensitive">{{ formatPhone(num.phone_number) }}</span>
            <span v-if="num.friendly_name" class="text-sm text-gray-500">{{ num.friendly_name }}</span>
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="statusBadgeClass(num.status)"
            >
              {{ statusLabel(num.status) }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <!-- Verify button -->
            <button
              v-if="num.status === 'unverified'"
              class="btn-primary-sm"
              :disabled="verifyingId === num.id"
              @click="startVerification(num)"
            >
              {{ verifyingId === num.id ? 'Starting...' : 'Verify' }}
            </button>

            <!-- Retry button -->
            <button
              v-if="num.status === 'verification_failed'"
              class="btn-secondary-sm"
              :disabled="verifyingId === num.id"
              @click="retryVerification(num)"
            >
              {{ verifyingId === num.id ? 'Retrying...' : 'Retry' }}
            </button>

            <!-- Disable button -->
            <button
              v-if="num.status === 'verified'"
              class="btn-icon text-sm"
              title="Disable this number"
              :disabled="disablingId === num.id"
              @click="handleDisable(num)"
            >
              <i class="fa-solid fa-ban" />
            </button>
          </div>
        </div>
      </div>

      <p v-else class="py-4 text-sm text-gray-500">No phone numbers added yet.</p>

      <!-- Verification in progress -->
      <div v-if="verificationCode" class="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm font-medium text-blue-900 mb-1">Twilio is calling you now</p>
        <p class="text-sm text-blue-700">
          When prompted, enter this code: <span class="font-mono font-bold text-lg">{{ verificationCode }}</span>
        </p>
        <p class="text-xs text-blue-500 mt-2">Waiting for verification... ({{ verificationCountdown }}s remaining)</p>
      </div>

      <!-- Add number form -->
      <div class="mt-4 pt-4 border-t border-gray-100">
        <div v-if="showAddForm" class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Phone Number</label>
              <input
                v-model="newNumber"
                type="tel"
                placeholder="+1 (555) 000-0000"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Label (optional)</label>
              <input
                v-model="newLabel"
                type="text"
                placeholder="e.g. Work Cell"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div class="flex gap-2">
            <button
              class="btn-primary-sm"
              :disabled="!newNumber.trim() || adding"
              @click="handleAdd"
            >
              {{ adding ? 'Adding...' : 'Add Number' }}
            </button>
            <button
              class="btn-secondary-sm"
              @click="showAddForm = false; newNumber = ''; newLabel = ''"
            >
              Cancel
            </button>
          </div>
        </div>
        <button
          v-else
          class="btn-secondary-sm"
          @click="showAddForm = true"
        >
          <i class="fa-solid fa-plus" />
          Add Phone Number
        </button>
      </div>

      <div v-if="error" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  listPhoneNumbers,
  createPhoneNumber,
  disablePhoneNumber,
  requestVerification,
  retryVerification,
} from '@/services/api/userPhoneNumbers';

export default {
  name: 'UserPhoneNumbers',
  props: {
    settings: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      phoneNumbers: [],
      loading: true,
      error: null,
      showAddForm: false,
      newNumber: '',
      newLabel: '',
      adding: false,
      verifyingId: null,
      verificationCode: null,
      verificationCountdown: 0,
      verificationPollTimer: null,
      verificationTimeoutTimer: null,
      disablingId: null,
    };
  },
  mounted() {
    this.fetchNumbers();
  },
  beforeUnmount() {
    this.stopVerificationPolling();
  },
  methods: {
    async fetchNumbers() {
      this.loading = true;
      try {
        this.phoneNumbers = await listPhoneNumbers();
      } catch {
        this.error = 'Failed to load phone numbers';
      } finally {
        this.loading = false;
      }
    },

    async handleAdd() {
      if (!this.newNumber.trim()) return;
      this.adding = true;
      this.error = null;
      try {
        await createPhoneNumber({
          phone_number: this.toE164(this.newNumber),
          friendly_name: this.newLabel.trim() || undefined,
        });
        this.newNumber = '';
        this.newLabel = '';
        this.showAddForm = false;
        await this.fetchNumbers();
      } catch (e) {
        this.error = e.response?.data?.phone_number?.[0] || e.response?.data?.detail || 'Failed to add number';
      } finally {
        this.adding = false;
      }
    },

    async startVerification(num) {
      this.verifyingId = num.id;
      this.error = null;
      try {
        const { validation_code } = await requestVerification(num.id);
        this.verificationCode = validation_code;
        this.startVerificationPolling(num.id);
      } catch {
        this.error = 'Failed to start verification';
      } finally {
        this.verifyingId = null;
      }
    },

    async retryVerification(num) {
      this.verifyingId = num.id;
      this.error = null;
      try {
        const { validation_code } = await retryVerification(num.id);
        this.verificationCode = validation_code;
        this.startVerificationPolling(num.id);
      } catch {
        this.error = 'Failed to retry verification';
      } finally {
        this.verifyingId = null;
      }
    },

    startVerificationPolling(numberId) {
      this.stopVerificationPolling();
      this.verificationCountdown = 120;

      this.verificationPollTimer = setInterval(async () => {
        try {
          const numbers = await listPhoneNumbers();
          const target = numbers.find(n => n.id === numberId);
          if (target && target.status === 'verified') {
            this.phoneNumbers = numbers;
            this.verificationCode = null;
            this.stopVerificationPolling();
          } else if (target && target.status === 'verification_failed') {
            this.phoneNumbers = numbers;
            this.verificationCode = null;
            this.error = 'Verification failed. You can retry.';
            this.stopVerificationPolling();
          }
        } catch {
          // Keep polling
        }
      }, 4000);

      this.verificationTimeoutTimer = setInterval(() => {
        this.verificationCountdown--;
        if (this.verificationCountdown <= 0) {
          this.verificationCode = null;
          this.error = 'Verification timed out. Please try again.';
          this.stopVerificationPolling();
          this.fetchNumbers();
        }
      }, 1000);
    },

    stopVerificationPolling() {
      if (this.verificationPollTimer) {
        clearInterval(this.verificationPollTimer);
        this.verificationPollTimer = null;
      }
      if (this.verificationTimeoutTimer) {
        clearInterval(this.verificationTimeoutTimer);
        this.verificationTimeoutTimer = null;
      }
    },

    async handleDisable(num) {
      this.disablingId = num.id;
      this.error = null;
      try {
        await disablePhoneNumber(num.id);
        await this.fetchNumbers();
      } catch {
        this.error = 'Failed to disable number';
      } finally {
        this.disablingId = null;
      }
    },

    toE164(phone) {
      if (!phone) return '';
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
      return phone;
    },

    formatPhone(phone) {
      if (!phone) return '';
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return phone;
    },

    statusLabel(status) {
      const labels = {
        verified: 'Verified',
        verification_requested: 'Pending',
        verification_failed: 'Failed',
        unverified: 'Unverified',
        disabled: 'Disabled',
      };
      return labels[status] || status;
    },

    statusBadgeClass(status) {
      const classes = {
        verified: 'bg-green-100 text-green-800',
        verification_requested: 'bg-yellow-100 text-yellow-800',
        verification_failed: 'bg-red-100 text-red-800',
        unverified: 'bg-gray-100 text-gray-600',
        disabled: 'bg-gray-100 text-gray-400 line-through',
      };
      return classes[status] || 'bg-gray-100 text-gray-600';
    },
  },
};
</script>
